// Yoga Westbury - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaWestbury = () => {
  usePageMeta({
    title: "Yoga Classes Westbury | Annelie Lily",
    description: "Yoga classes for Westbury, Wiltshire. Small group Hatha & Yin yoga in Frome, about 20 minutes via the A36. Personal attention, beginners welcome.",
    canonical: "https://annelielily.com/areas/yoga-westbury"
  });

  const locationSchema = generateLocationSchema({
    townName: "Westbury",
    description: "Yoga classes for Westbury at Annelie Lily Yoga in Frome. Small group classes and private sessions about 20 minutes from Westbury.",
    url: "https://annelielily.com/yoga-westbury"
  });

  const faqs = [
    {
      question: "What's the best route from Westbury to Frome?",
      answer: "Take the A36 south, then follow signs for Frome. It's about 20 minutes — a straightforward route through rural Wiltshire and Somerset."
    },
    {
      question: "Do you offer evening classes?",
      answer: "Yes, I run classes at various times including evenings, which works well for those coming from Westbury after work. Get in touch for the current schedule."
    },
    {
      question: "What if I need to cancel a class?",
      answer: "I ask for 24 hours notice if possible. Life happens — but advance notice helps me offer your spot to someone else."
    },
    {
      question: "Are classes suitable for men?",
      answer: "Absolutely. Yoga isn't just for women — I have male students of various ages. Everyone is welcome."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Westbury" currentSlug="areas/yoga-westbury" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Westbury</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Westbury</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Westbury to Frome is about 20 minutes via the A36. Come for small, personal yoga 
              classes where you'll actually get individual attention — not just instructions 
              aimed at a room of 30.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />20 min from Westbury</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Westbury Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Westbury sits just over the border in Wiltshire, but Frome is an easy 20-minute 
              drive. If you're looking for <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> that 
              feel more personal than what's available locally, it's worth the journey.
            </p>
            <p>
              My classes are kept small — usually 6-8 people — because yoga works better when 
              there's space for individual guidance. I can see what's happening in your practice 
              and offer adjustments that actually help.
            </p>
            <p>
              Whether you prefer the more active <Link to="/areas/yoga-westbury/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Westbury</Link> or 
              the slower, deeper work of <Link to="/areas/yoga-westbury/yin-yoga" className="text-gold hover:underline">Yin yoga in Westbury</Link>, you'll 
              find a calm and welcoming environment. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">New to yoga?</Link> That's 
              perfectly fine — I'll guide you through everything.
            </p>
            <p>
              The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Westbury</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga Westbury", desc: "Active practice combining postures, breathing, and relaxation. Builds strength and flexibility over time.", link: "/areas/yoga-westbury/hatha-yoga" },
              { title: "Yin Yoga Westbury", desc: "Slow, meditative practice with long-held stretches. Wonderful for releasing deep-held tension.", link: "/areas/yoga-westbury/yin-yoga" },
              { title: "Private Sessions Westbury", desc: "One-to-one yoga designed entirely around your needs and goals.", link: "/areas/yoga-westbury/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Questions from Westbury Students
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaWestbury;
